import { useEffect, useMemo, useRef, useState } from 'react';
import { openUrlInNewTab } from '../utils/openDocument';
import { supabase } from '@/integrations/supabase/client';
import { useCrm } from '../store';
import { Card, PageTitle } from '../ui';
import { useConfirm } from '../components/ConfirmDialog';
import { formatDateFR, formatEUR } from '../utils';
import { FileSignature, Plus, Download, Upload, Eye, Trash2, Edit3, FileText, Search, RefreshCcw, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import ContratArtisteForm from '../components/ContratArtisteForm';

export type ContratArtisteRow = {
  id: string;
  numero: string;
  prestation_id: string | null;
  artiste_id: string | null;
  artiste_nom: string;
  artiste_email: string | null;
  client_final: string | null;
  nature_prestation: string | null;
  date_prestation: string | null;
  lieu: string | null;
  remuneration_ht: number | null;
  remuneration_ttc: number | null;
  statut: string;
  date_emission: string;
  date_envoi: string | null;
  date_signature: string | null;
  pdf_url: string | null;
  pdf_signe_url: string | null;
  contenu: any;
  notes: string | null;
};

const STATUTS: { value: string; label: string; cls: string }[] = [
  { value: 'Brouillon', label: 'Brouillon', cls: 'bg-slate-700/40 text-slate-300 border-slate-600/40' },
  { value: 'À envoyer', label: 'À envoyer', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  { value: 'Envoyé', label: 'Envoyé', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  { value: 'Signé', label: 'Signé', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  { value: 'Refusé', label: 'Refusé', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  { value: 'Annulé', label: 'Annulé', cls: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40' },
];

function StatutInlineSelect({ row, onChanged }: { row: ContratArtisteRow; onChanged: (s: string) => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const meta = STATUTS.find((x) => x.value === row.statut) || STATUTS[0];

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const change = async (next: string) => {
    setOpen(false);
    if (next === row.statut) return;
    setBusy(true);
    const patch: any = { statut: next };
    if (next === 'Envoyé' && !row.date_envoi) patch.date_envoi = new Date().toISOString().slice(0, 10);
    if (next === 'Signé' && !row.date_signature) patch.date_signature = new Date().toISOString().slice(0, 10);
    const { error } = await supabase.from('contrats_artistes' as any).update(patch).eq('id', row.id);
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success(`Statut → ${next}`);
    onChanged(next);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${meta.cls} disabled:opacity-50`}
      >
        {meta.label}
        <ChevronDown size={10} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 left-0 bg-slate-900 border border-slate-700 rounded-lg shadow-xl py-1 min-w-[140px]">
          {STATUTS.map((s) => (
            <button
              key={s.value}
              onClick={() => change(s.value)}
              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-800 ${s.value === row.statut ? 'text-amber-300' : 'text-slate-300'}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContratsArtistes() {
  const data = useCrm();
  const confirm = useConfirm();
  const [rows, setRows] = useState<ContratArtisteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState<'all' | string>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ContratArtisteRow | null>(null);
  const [prefill, setPrefill] = useState<{ prestation_id: string; artiste_id?: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data: result, error } = await supabase
      .from('contrats_artistes' as any)
      .select('*')
      .is('deleted_at', null)
      .order('date_emission', { ascending: false });
    if (error) toast.error(`Erreur chargement : ${error.message}`);
    setRows(((result as any) || []) as ContratArtisteRow[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statutFilter !== 'all' && r.statut !== statutFilter) return false;
      if (!q) return true;
      const hay = [r.numero, r.artiste_nom, r.client_final, r.lieu, r.nature_prestation]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, statutFilter]);

  const openNew = () => { setEditing(null); setPrefill(null); setFormOpen(true); };
  const openEdit = (r: ContratArtisteRow) => { setEditing(r); setPrefill(null); setFormOpen(true); };
  const openFromPrestation = (prestation_id: string) => {
    setEditing(null);
    setPrefill({ prestation_id });
    setFormOpen(true);
  };

  const remove = async (r: ContratArtisteRow) => {
    const ok = await confirm({ title: 'Supprimer ce contrat artiste ?', description: `${r.numero} — ${r.artiste_nom}`, confirmLabel: 'Supprimer', destructive: true });
    if (!ok) return;
    const { error } = await supabase.from('contrats_artistes' as any).update({ deleted_at: new Date().toISOString() }).eq('id', r.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Contrat supprimé');
    await load();
  };

  const downloadGenerated = async (r: ContratArtisteRow) => {
    setBusyId(r.id);
    try {
      const { genererContratArtistePdf } = await import('../contract/generateContratArtistePdf');
      const fileName = `${r.numero}-${(r.artiste_nom || 'artiste').replace(/[^a-z0-9]+/gi, '-')}.pdf`;
      await genererContratArtistePdf(r.contenu || {}, fileName);
    } catch (e: any) {
      toast.error(`Échec PDF : ${e.message || e}`);
    } finally {
      setBusyId(null);
    }
  };

  const previewGenerated = async (r: ContratArtisteRow) => {
    try {
      const { previewContratArtisteHtml } = await import('../contract/generateContratArtistePdf');
      previewContratArtisteHtml(r.contenu || {});
    } catch (e: any) {
      toast.error(`Échec aperçu : ${e.message || e}`);
    }
  };

  const uploadSigned = async (r: ContratArtisteRow, file: File) => {
    setBusyId(r.id);
    try {
      const path = `artistes/${r.id}/${Date.now()}-${file.name}`;
      const { error: upErr } = await supabase.storage.from('contrats-pdf').upload(path, file, { upsert: true, contentType: file.type || 'application/pdf' });
      if (upErr) throw upErr;
      const patch: any = { pdf_signe_url: path, statut: 'Signé', date_signature: new Date().toISOString().slice(0, 10) };
      const { error } = await supabase.from('contrats_artistes' as any).update(patch).eq('id', r.id);
      if (error) throw error;
      toast.success('Contrat signé téléversé');
      await load();
    } catch (e: any) {
      toast.error(`Échec upload : ${e.message || e}`);
    } finally {
      setBusyId(null);
    }
  };

  const downloadSigned = async (r: ContratArtisteRow) => {
    if (!r.pdf_signe_url) return;
    const { data: signed } = await supabase.storage.from('contrats-pdf').createSignedUrl(r.pdf_signe_url, 3600);
    if (signed?.signedUrl) openUrlInNewTab(signed.signedUrl);
  };

  const totalHT = (r: ContratArtisteRow) => {
    if (r.remuneration_ht != null) return Number(r.remuneration_ht);
    if (r.remuneration_ttc != null) return Number(r.remuneration_ttc) / 1.2;
    return 0;
  };

  return (
    <div>
      <PageTitle
        title="Contrats artistes"
        subtitle="Génération, envoi manuel et suivi des retours signés"
        icon={<FileSignature className="text-amber-400" size={24} strokeWidth={1.5} />}
        action={
          <button onClick={openNew} className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 rounded-lg text-sm text-amber-300 flex items-center gap-2">
            <Plus size={14} strokeWidth={1.5} /> Nouveau contrat artiste
          </button>
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher client, numéro, artiste, lieu..."
              className="w-full pl-9 pr-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <select
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value)}
            className="px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200"
          >
            <option value="all">Tous statuts</option>
            {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            onChange={(e) => { if (e.target.value) { openFromPrestation(e.target.value); e.target.value = ''; } }}
            className="px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-200"
            defaultValue=""
          >
            <option value="">+ Depuis une prestation…</option>
            {data.prest.filter((p) => !p.archivedAt).map((p) => (
              <option key={p.id} value={p.id}>{p.id} — {p.client}{p.dateEvent ? ` (${formatDateFR(p.dateEvent)})` : ''}</option>
            ))}
          </select>
          <button onClick={load} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-slate-300 flex items-center gap-1.5">
            <RefreshCcw size={13} strokeWidth={1.5} /> Recharger
          </button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            Aucun contrat artiste. <button onClick={openNew} className="text-amber-400 hover:underline">Créer le premier</button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-slate-500 uppercase tracking-widest border-b border-slate-800 bg-slate-950/40">
                <th className="text-left py-3 px-4 font-normal">Statut</th>
                <th className="text-left py-3 px-4 font-normal">Client / Événement</th>
                <th className="text-left py-3 px-4 font-normal">Créée le</th>
                <th className="text-left py-3 px-4 font-normal">Date event</th>
                <th className="text-right py-3 px-4 font-normal">Total HT</th>
                <th className="text-left py-3 px-4 font-normal">Version</th>
                <th className="text-right py-3 px-4 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/50 last:border-0 hover:bg-slate-800/20">
                  <td className="py-3 px-4">
                    <StatutInlineSelect row={r} onChanged={(next) => setRows((arr) => arr.map((x) => x.id === r.id ? { ...x, statut: next } : x))} />
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-slate-200">{r.artiste_nom || '—'}</div>
                    <div className="text-xs text-slate-500">
                      {[r.client_final, r.nature_prestation, r.lieu].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-400 text-xs whitespace-nowrap">
                    {r.date_emission ? (
                      <>
                        {formatDateFR(r.date_emission, { day: '2-digit', month: '2-digit', year: '2-digit' })}
                        <span className="text-slate-500"> · {new Date(r.date_emission).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                      </>
                    ) : '—'}
                  </td>
                  <td className="py-3 px-4 text-slate-400">{r.date_prestation ? formatDateFR(r.date_prestation) : '—'}</td>
                  <td className="py-3 px-4 text-right text-amber-400 font-medium tabular-nums">{formatEUR(totalHT(r))}</td>
                  <td className="py-3 px-4 text-slate-400 text-xs">{r.numero}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(r)} title="Éditer" className="p-1.5 rounded hover:bg-slate-700 text-slate-300"><Edit3 size={14} strokeWidth={1.5} /></button>
                      <button onClick={() => previewGenerated(r)} title="Aperçu" className="p-1.5 rounded hover:bg-slate-700 text-slate-300"><Eye size={14} strokeWidth={1.5} /></button>
                      <button onClick={() => downloadGenerated(r)} disabled={busyId === r.id} title="PDF" className="p-1.5 rounded hover:bg-amber-500/20 text-amber-300 disabled:opacity-50"><Download size={14} strokeWidth={1.5} /></button>
                      <label title={r.pdf_signe_url ? 'Remplacer signé' : 'Téléverser signé'} className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-300 cursor-pointer">
                        <Upload size={14} strokeWidth={1.5} />
                        <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => {
                          const f = e.target.files?.[0]; if (f) uploadSigned(r, f); e.target.value = '';
                        }} />
                      </label>
                      {r.pdf_signe_url && (
                        <button onClick={() => downloadSigned(r)} title="Voir signé" className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-300"><FileText size={14} strokeWidth={1.5} /></button>
                      )}
                      <button onClick={() => remove(r)} title="Supprimer" className="p-1.5 rounded hover:bg-red-500/20 text-red-300"><Trash2 size={14} strokeWidth={1.5} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {formOpen && (
        <ContratArtisteForm
          existing={editing}
          prefill={prefill}
          onClose={() => { setFormOpen(false); setPrefill(null); }}
          onSaved={async () => { setFormOpen(false); setPrefill(null); await load(); }}
        />
      )}
    </div>
  );
}
